import "./Dashboard.scss";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { useDashboardDateRange } from "../context/DashboardDateRangeContext";
import { QualityChecksSummary, QualityChecksTotalCountSummary } from "../model/qualityCheckSummaryDto";
import { DateRange } from "../../../core/types";
import StatisticApi from "../api/statisticApi";
import { QualityCheckStatus } from "../../qualitycheck/model/qualityCheckDtos";
import { DashboardActionBar } from "../component/actionbar/DashboardActionBar";
import { DashboardTickerCollection } from "../component/ticker/DashboardTickerCollection";
import { BarChart } from "../component/statistic/BarChart";
import { Card } from "../../../core/components";


export default function Dashboard() {
    const dateRangeContext = useDashboardDateRange()
    const [qualityCheckStatesByDates, setQualityCheckStatesByDates] = useState<QualityChecksSummary[]>([]);
    const [qualityChecksTotalCountByDates, setQualityChecksTotalCountByDates] = useState<QualityChecksTotalCountSummary[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);

    const [isExpiredVisible, setIsExpiredVisible] = useState(false)
    const [isFinishedVisible, setIsFinishedVisible] = useState(true)
    const [isActiveVisible, setIsActiveVisible] = useState(true)
    const [isScheduledVisible, setIsScheduledVisible] = useState(true)


    useEffect(() => {
        getQualityCheckStatusesByDates(dateRangeContext.dateRange);
    }, [dateRangeContext.dateRange]);

    const getQualityCheckStatusesByDates = async (dateRange: DateRange) => {
        const start = dateRange[0]
        const end = dateRange[1]

        if (!start || !end)
            return;

        try {
            setLoading(true);

            const startDateFormatted = start.toISOString().split("T")[0];
            const endDateFormatted = end.toISOString().split("T")[0];

            const qualityCheckStatesByDates = await StatisticApi.getQualityCheckStatusesByDates(startDateFormatted, endDateFormatted);
            setQualityCheckStatesByDates(qualityCheckStatesByDates);

            const qualityChecksTotalCountByDates = processData(qualityCheckStatesByDates);
            setQualityChecksTotalCountByDates(qualityChecksTotalCountByDates);
        } finally {
            setLoading(false);
        }
    }

    const processData = (data: QualityChecksSummary[]): QualityChecksTotalCountSummary[] => {
        return data.map(item => ({
            scheduledOn: item.scheduledOn,
            totalCount: item.active + item.expired + item.scheduled + item.finished
        }))
    }

    const refresh = () => {
        setQualityCheckStatesByDates([]);
        setQualityChecksTotalCountByDates([]);
        getQualityCheckStatusesByDates(dateRangeContext.dateRange);
    }

    const updateSelectedStatuses = (status: QualityCheckStatus) => {
        switch (status) {
            case QualityCheckStatus.Expired:
                setIsExpiredVisible(prev => !prev)
                break;
            case QualityCheckStatus.Finished:
                setIsFinishedVisible(prev => !prev)
                break;
            case QualityCheckStatus.Active:
                setIsActiveVisible(prev => !prev)
                break;
            case QualityCheckStatus.Scheduled:
                setIsScheduledVisible(prev => !prev)
                break;
        }
    }

    return (
        <>
            <DashboardActionBar refreshCallback={refresh} isLoading={isLoading} />

            <DashboardTickerCollection
                items={qualityCheckStatesByDates}
                isExpiredSelected={isExpiredVisible}
                isFinishedSelected={isFinishedVisible}
                isActiveSelected={isActiveVisible}
                isScheduledSelected={isScheduledVisible}
                onSelectionChanged={updateSelectedStatuses} />

            <Card padding={2}>
                <BarChart
                    data={qualityCheckStatesByDates}
                    title={t("Quality Check States by Dates")}
                    exportEnabled
                    tooltipEnabled
                    customizeTooltip={(arg) => {
                        return { text: `${arg.seriesName}: ${arg.valueText}\n${arg.argumentText}` }
                    }}
                    series={[
                        { valueField: "expired", name: t("Expired"), visible: isExpiredVisible },
                        { valueField: "finished", name: t("Finished"), visible: isFinishedVisible },
                        { valueField: "active", name: t("Active"), visible: isActiveVisible },
                        { valueField: "scheduled", name: t("Scheduled"), visible: isScheduledVisible }
                    ]} />
            </Card>

            <Card padding={2}>
                <BarChart
                    data={qualityChecksTotalCountByDates}
                    title={t("Quality Checks Total Count by Dates")}
                    exportEnabled
                    tooltipEnabled
                    customizeTooltip={(arg) => {
                        return { text: `${arg.seriesName}: ${arg.valueText}\n${arg.argumentText}` }
                    }}
                    series={[
                        { valueField: "totalCount", name: t("Total Count") }
                    ]} />
            </Card>

            <Card padding={2}>
                <BarChart
                    data={qualityCheckStatesByDates}
                    title={t("Quality Check States by Dates (stacked)")}
                    exportEnabled
                    tooltipEnabled
                    seriesType="stackedbar"
                    customizeTooltip={(arg) => {
                        return { text: `${arg.seriesName}: ${arg.valueText}\n${arg.argumentText}` }
                    }}
                    series={[
                        { valueField: "expired", name: t("Expired"), visible: isExpiredVisible },
                        { valueField: "finished", name: t("Finished"), visible: isFinishedVisible },
                        { valueField: "active", name: t("Active"), visible: isActiveVisible },
                        { valueField: "scheduled", name: t("Scheduled"), visible: isScheduledVisible }
                    ]} />
            </Card>

        </>
    );
}
